import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import ClaimStatusBadge from './ClaimStatusBadge';
import ClaimDetailPanel from './ClaimDetailPanel';
import ClaimStatusDialog from './ClaimStatusDialog';
import ClaimCorrectionDialog from './ClaimCorrectionDialog';
import ReasonDialog from '../../common/ReasonDialog';
import { LoadingSpinner } from '../../common';
import { ClaimsService } from '../../../services/billing/claims.service';
import { usePermission } from '../../../hooks/usePermission';
import { CLAIM_STATUS_BADGE } from '../../../dtos/billing/claims.dto';
import type { Claim, ClaimDetail, ClaimStatus, ClaimsListParams } from '../../../dtos/billing/claims.dto';
import type { PermissionString } from '../../../types/permissions';

const PAGE_SIZE = 25;
const STATUS_FILTERS: Array<ClaimStatus | ''> = ['', 'GENERATED', 'SUBMITTED', 'ACCEPTED', 'PAID', 'DENIED', 'REJECTED', 'VOID', 'CORRECTED'];
const UPDATE = ['billing:claims:update-status', 'billing:claims:update'] as PermissionString[];
const VOID = 'billing:claims:delete' as PermissionString;
const CORRECT = 'billing:claims:generate' as PermissionString;

const inputClass = 'w-full rounded-md border border-slate-300 px-2 py-1.5 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500';

interface ClaimsRegisterProps {
  /** Claim to open first (e.g. just generated) */
  initialClaimId?: string | null;
}

/**
 * Tenant claims register (REQ-BIL-10): filter by patient, payer, status and date of service;
 * manual status updates with history; void unsubmitted claims; corrected versions; PDF download.
 */
const ClaimsRegister: React.FC<ClaimsRegisterProps> = ({ initialClaimId = null }) => {
  const canUpdate = usePermission(UPDATE);
  const canVoid = usePermission(VOID);
  const canCorrect = usePermission(CORRECT);

  const [filters, setFilters] = useState<ClaimsListParams>({});
  const [draft, setDraft] = useState<ClaimsListParams>({});
  const [offset, setOffset] = useState(0);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [detail, setDetail] = useState<ClaimDetail | null>(null);
  const [statusClaim, setStatusClaim] = useState<Claim | null>(null);
  const [correctClaim, setCorrectClaim] = useState<Claim | null>(null);
  const [voidTarget, setVoidTarget] = useState<Claim | null>(null);
  const [voiding, setVoiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ClaimsService.listClaims({ ...filters, limit: PAGE_SIZE, offset });
      setClaims(result.claims);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (claimId: string) => {
    try {
      setDetail(await ClaimsService.getDetail(claimId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the claim');
    }
  }, []);

  useEffect(() => { if (initialClaimId) openDetail(initialClaimId); }, [initialClaimId, openDetail]);

  const refresh = async (claimId: string, message: string) => {
    setNotice(message);
    await Promise.all([load(), openDetail(claimId)]);
  };

  const download = async (claimId: string) => {
    try {
      const { signedUrl } = await ClaimsService.getClaimDownloadUrl(claimId);
      window.open(signedUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download the claim');
    }
  };

  const confirmVoid = async (reason: string) => {
    if (!voidTarget) return;
    setVoiding(true);
    try {
      await ClaimsService.voidClaim(voidTarget.claimId, reason);
      const id = voidTarget.claimId;
      setVoidTarget(null);
      await refresh(id, 'Claim voided. Its visits can be billed again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void the claim');
      setVoidTarget(null);
    } finally {
      setVoiding(false);
    }
  };

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setFilters({ ...draft });
  };

  return (
    <div className="space-y-4 p-4 text-left" data-testid="claims-register">
      <form onSubmit={applyFilters} className="grid grid-cols-1 gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 md:grid-cols-6" aria-label="Claim filters">
        <div className="md:col-span-2">
          <label htmlFor="reg-search" className="mb-1 block text-xs font-medium text-slate-700">Patient, MRN or claim #</label>
          <input id="reg-search" type="search" value={draft.search || ''} onChange={(e) => setDraft({ ...draft, search: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-payer" className="mb-1 block text-xs font-medium text-slate-700">Payer</label>
          <input id="reg-payer" type="text" value={draft.payer || ''} onChange={(e) => setDraft({ ...draft, payer: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-status" className="mb-1 block text-xs font-medium text-slate-700">Status</label>
          <select id="reg-status" value={draft.status || ''} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className={inputClass}>
            {STATUS_FILTERS.map((s) => <option key={s || 'all'} value={s}>{s ? CLAIM_STATUS_BADGE[s].label : 'All statuses'}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="reg-from" className="mb-1 block text-xs font-medium text-slate-700">Service from</label>
          <input id="reg-from" type="date" value={draft.from || ''} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-to" className="mb-1 block text-xs font-medium text-slate-700">Service to</label>
          <input id="reg-to" type="date" value={draft.to || ''} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className={inputClass} />
        </div>
        <div className="flex gap-2 md:col-span-6">
          <button type="submit" className="flex items-center gap-1 rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white"><Search size={14} /> Search</button>
          <button type="button" onClick={() => { setDraft({}); setFilters({}); setOffset(0); }} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">Clear</button>
        </div>
      </form>

      {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {notice && <p role="status" className="rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-800">{notice}</p>}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Patient</th>
              <th className="px-3 py-2 text-left">Payer</th>
              <th className="px-3 py-2 text-left">Dates of service</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Generated</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => (
              <tr
                key={c.claimId}
                onClick={() => openDetail(c.claimId)}
                className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${detail?.claimId === c.claimId ? 'bg-sky-50' : ''}`}
              >
                <td className="px-3 py-2">
                  <button type="button" className="text-left font-medium text-slate-800 hover:underline" onClick={(e) => { e.stopPropagation(); openDetail(c.claimId); }}>
                    {c.patientName}
                  </button>
                  <span className="block text-xs text-slate-500">{c.mrn}{c.version && c.version > 1 ? ` · v${c.version}` : ''}</span>
                </td>
                <td className="px-3 py-2">{c.payer?.payerName || c.payerType}</td>
                <td className="px-3 py-2">{ClaimsService.formatDate(c.dosFrom)}{c.dosTo && c.dosTo !== c.dosFrom ? `–${ClaimsService.formatDate(c.dosTo)}` : ''}</td>
                <td className="px-3 py-2 text-right">{ClaimsService.formatCurrency(c.totalAmount)}</td>
                <td className="px-3 py-2">
                  <ClaimStatusBadge status={c.status} />
                  {c.overrideFlag && <span className="ml-1 text-xs text-amber-700" title="EVV override on this claim">⚑</span>}
                </td>
                <td className="px-3 py-2 text-slate-500">{new Date(c.generatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {!loading && claims.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-slate-500">No claims match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loading && <LoadingSpinner className="justify-start" />}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-2">
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="rounded border border-slate-300 px-2 py-1 disabled:opacity-50">Previous</button>
            <button type="button" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} className="rounded border border-slate-300 px-2 py-1 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {detail && (
        <ClaimDetailPanel
          claim={detail}
          canUpdate={canUpdate}
          canVoid={canVoid}
          canCorrect={canCorrect}
          onClose={() => setDetail(null)}
          onUpdateStatus={() => setStatusClaim(detail)}
          onVoid={() => setVoidTarget(detail)}
          onCorrect={() => setCorrectClaim(detail)}
          onDownload={() => download(detail.claimId)}
          onOpenVersion={openDetail}
        />
      )}

      <ClaimStatusDialog
        claim={statusClaim}
        onClose={() => setStatusClaim(null)}
        onSaved={(saved) => { setStatusClaim(null); refresh(saved.claimId, 'Claim updated.'); }}
      />
      <ClaimCorrectionDialog
        claim={correctClaim}
        onClose={() => setCorrectClaim(null)}
        onCreated={({ claim }) => { setCorrectClaim(null); refresh(claim.claimId, `Corrected claim version ${claim.version} created.`); }}
      />
      <ReasonDialog
        open={Boolean(voidTarget)}
        title="Void this claim?"
        description="Only claims not yet submitted can be voided. Its visits become billable again."
        confirmLabel="Void claim"
        minLength={3}
        maxLength={500}
        destructive
        busy={voiding}
        onConfirm={confirmVoid}
        onCancel={() => setVoidTarget(null)}
      />
    </div>
  );
};

export default ClaimsRegister;
